import formatToCNPJ from "./formatToCNPJ";
import formatToCNPJAlphanumeric from "./formatToCNPJAlphanumeric";
import formatToCPF from "./formatToCPF";

/**
 * Check if a `string` value can be formatted to CPF.
 * @param value - A `string` value of a CPF or CNPJ.
 */
const canFormatToCPF = (value: string): boolean =>
  (value.match(/\d/g)?.length ?? 0) <= 11;

/**
 * Check if a `string` value contains alphanumeric characters (letters).
 * @param value - A `string` value of a CNPJ.
 */
const hasAlphanumericChars = (value: string): boolean => /[A-Za-z]/.test(value);

/**
 * Formats step-by-step a `string` value to CPF or CNPJ depending on its length.
 * Supports numeric CPFs, numeric CNPJs, and alphanumeric CNPJs.
 * @example
 * formatToCPFOrCNPJ('00000000')
 * //=> '000.000.00'
 *
 * formatToCPFOrCNPJ('366.418.768-70')
 * //=> '366.418.768-70'
 *
 * formatToCPFOrCNPJ('32284981000138')
 * //=> '32.284.981/0001-38'
 *
 * formatToCPFOrCNPJ('00.0.000.00.00--00-00')
 * //=> '00.000.000/0000-00'
 *
 * formatToCPFOrCNPJ('AB12CD34EF5602')
 * //=> 'AB.12C.D34/EF56-02'
 * @param value - A `string` value of a CPF or CNPJ.
 */
const formatToCPFOrCNPJ = (value: string): string => {
  // First check if it contains letters (alphanumeric CNPJ)
  if (hasAlphanumericChars(value)) {
    return formatToCNPJAlphanumeric(value);
  }

  // Then check if it's a CPF based on digit count
  if (canFormatToCPF(value)) {
    return formatToCPF(value);
  }

  // Otherwise, format as numeric CNPJ
  return formatToCNPJ(value);
};

export default formatToCPFOrCNPJ;
